import * as THREE from "three";
import { ARENA, OBSTACLES, terrainHeight } from "@tank/shared";

// Purely decorative — instanced grass blades scattered across the arena floor,
// kept clear of obstacles/spawns so nothing pokes through geometry. Small
// module (per the "keep visuals modular" convention) so it can be tuned or
// swapped independently of ArenaView's ground/obstacle meshes.
const BLADE_COUNT = 4000;
const BLADE_WIDTH = 0.14;
const BLADE_HEIGHT = 0.55;

function buildBladeGeometry(): THREE.BufferGeometry {
  // A single tapered triangle (wide base, narrow tip) is enough to read as a
  // grass blade at gameplay camera distance, and is essentially free to render
  // thousands of via instancing.
  const w = BLADE_WIDTH / 2;
  const positions = new Float32Array([
    -w, 0, 0,
    w, 0, 0,
    0, BLADE_HEIGHT, 0.05,
  ]);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setIndex([0, 1, 2]);
  geo.computeVertexNormals();
  return geo;
}

// Obstacles get a clearance radius so grass doesn't clip through their meshes.
const CLEAR_ZONES: ReadonlyArray<{ x: number; z: number; r: number }> = OBSTACLES.map((o) => ({
  x: o.x,
  z: o.z,
  r: Math.hypot(o.w, o.d) / 2 + 1.2,
}));

function isClear(x: number, z: number): boolean {
  for (const c of CLEAR_ZONES) {
    if (Math.hypot(x - c.x, z - c.z) < c.r) return false;
  }
  return true;
}

/** Scatters instanced grass blades across the arena (clear of obstacles). */
export function buildGrassField(scene: THREE.Scene): void {
  const geo = buildBladeGeometry();
  const mat = new THREE.MeshStandardMaterial({ color: 0x5a9c46, roughness: 0.9, side: THREE.DoubleSide });
  const mesh = new THREE.InstancedMesh(geo, mat, BLADE_COUNT);
  mesh.receiveShadow = true;

  const half = ARENA.HALF - 1.2;
  const dummy = new THREE.Object3D();
  let placed = 0;
  let attempts = 0;
  while (placed < BLADE_COUNT && attempts < BLADE_COUNT * 5) {
    attempts++;
    const x = (Math.random() * 2 - 1) * half;
    const z = (Math.random() * 2 - 1) * half;
    if (!isClear(x, z)) continue;
    dummy.position.set(x, terrainHeight(x, z), z);
    dummy.rotation.y = Math.random() * Math.PI * 2;
    const s = 0.7 + Math.random() * 0.6;
    dummy.scale.set(s, s * (0.75 + Math.random() * 0.5), s);
    dummy.updateMatrix();
    mesh.setMatrixAt(placed, dummy.matrix);
    placed++;
  }
  mesh.count = placed;
  mesh.instanceMatrix.needsUpdate = true;
  scene.add(mesh);
}
